import { createContext, useContext, ReactNode } from 'react'
import { AnexApiClient } from '@anex/common'

import { AccountStore } from './AccountStore'
import { AppStore } from './AppStore'
import { ServiceStore } from './ServiceStore'
import { UsageStore } from './UsageStore'
import { BillingStore } from './BillingStore'
import { Constants } from '~/utils/Constants'

export class RootStore {
  public api: AnexApiClient
  public app: AppStore
  public account: AccountStore
  public service: ServiceStore
  public usage: UsageStore
  public billing: BillingStore

  constructor() {
    // Set it here to make sure that before our API call
    // we are already setting the proper endpoints and environment
    if (import.meta.env.DEV) {
      ;(window as any)['ANEX_API_ENDPOINT'] = import.meta.env.ANEX_API_ENDPOINT
      ;(window as any)['NODE_ENV'] = import.meta.env.MODE
    }
    this.api = new AnexApiClient(Constants.ANEX_API_ENDPOINT)
    this.account = new AccountStore(this)
    this.service = new ServiceStore(this)
    this.usage = new UsageStore(this)
    this.billing = new BillingStore(this)
    this.app = new AppStore(this)
  }
}

export const store = new RootStore()

// Create Root Store Context
const RootStoreContext = createContext<RootStore>(store)

// Create the provider component
export function RootStoreProvider({ children }: { children: ReactNode }) {
  return (
    <RootStoreContext.Provider value={store}>
      {children}
    </RootStoreContext.Provider>
  )
}

// Create hook to use on our components
export function useRootStore() {
  const context = useContext(RootStoreContext)
  if (context === undefined) {
    throw new Error('useRootStore must be used within RootStoreProvider')
  }

  return context
}
